/**
 * Terms of Service and Privacy Policy.
 *
 * Written from what the product actually does, which is the only way a legal
 * page is worth anything: every statement here corresponds to behaviour that
 * exists in the codebase, and where behaviour is uncomfortable — an
 * append-only ledger that erasure cannot fully reach, retention that is a
 * target rather than a promise — it is stated rather than smoothed over.
 *
 * **These are a draft for counsel to review, not approved legal text.** The
 * PRD flags this explicitly (C02/C07), and the pages say so to the reader
 * too.
 *
 * The operator is a California LLC, which shapes two clauses that would
 * otherwise be boilerplate. California's automatic-renewal law requires the
 * renewal terms to be disclosed before purchase, consented to separately, and
 * cancellable online — which the product already does, so section 6 of the
 * Terms states them rather than burying them in the fees clause. And the
 * Privacy Policy carries a California rights section that says plainly we do
 * not sell or share personal information, instead of adding a "Do Not Sell"
 * link that would do nothing.
 */
import type { Block } from './blocks';

export type LegalDocument = {
  slug: 'terms' | 'privacy';
  title: string;
  description: string;
  updated: string;
  summary: string[];
  body: Block[];
};

/** Facts about the operator.
 *
 *  No registered address is published. An email address is a sufficient
 *  contact route for these documents, and transactional mail — invitations,
 *  passcodes, receipts — carries no postal-address requirement. That changes
 *  the day this product sends its first promotional email: US anti-spam law
 *  requires a valid physical postal address in commercial messages, and the
 *  address would have to appear then.
 */
export const COMPANY = {
  entity: 'Verticos.ai LLC',
  entityLong: 'Verticos.ai LLC, a California limited liability company',
  governingLaw:
    'the laws of the State of California, without regard to its conflict of '
    + 'laws rules. The exclusive venue for any dispute is the state and '
    + 'federal courts located in California',
  contact: 'support@verticos.ai',
};

const DRAFT_NOTICE: Block = {
  kind: 'note', tone: 'warn', title: 'Draft pending legal review',
  text: 'This document describes how the service actually behaves and has not '
    + 'yet been reviewed by a lawyer. Do not rely on it as final contractual '
    + 'wording.',
};

export const TERMS: LegalDocument = {
  slug: 'terms',
  title: 'Terms of Service',
  description:
    'The agreement between you and Verticos.ai LLC for use of eSignLoop: what '
    + 'the service does, what it costs, what we commit to and what we do not.',
  updated: '2026-10-20',
  summary: [
    'You keep ownership of your documents. We process them to provide the service.',
    'Credits are prepaid and never expire. Plans renew annually until cancelled.',
    'Retention is a seven-year target, not a guarantee — export anything you '
      + 'need to be sure of.',
    'We do not give legal advice and make no claim that any agreement is '
      + 'enforceable anywhere in particular.',
  ],
  body: [
    DRAFT_NOTICE,
    { kind: 'p', text:
      `These terms govern your use of eSignLoop, operated by `
      + `${COMPANY.entityLong} ("we", "us"). By creating an account or sending `
      + `an agreement you accept them. If you do not accept them, do not use `
      + `the service.` },

    { kind: 'h', text: '1. What the service does' },
    { kind: 'p', text:
      'eSignLoop lets you upload documents, place fields, send them for '
      + 'electronic signature, and retrieve the completed document together '
      + 'with an evidence record. The evidence record includes integrity '
      + 'digests of the documents, an append-only audit trail of events, and '
      + 'material allowing the record to be verified independently.' },

    { kind: 'h', text: '2. Accounts and responsibility' },
    { kind: 'ul', items: [
      'You must be able to enter a binding contract, and you are responsible '
        + 'for activity under your account.',
      'You are responsible for the accuracy of recipient email addresses. We '
        + 'deliver to the address you supply and cannot know it is the right '
        + 'person.',
      'Administrators must enrol a second authentication factor. This is '
        + 'required, not optional.',
      'Tell us promptly if you believe an account, API key or signing link '
        + 'has been compromised.',
    ] },

    { kind: 'h', text: '3. Your content' },
    { kind: 'p', text:
      'You retain all rights in the documents and data you upload. You grant '
      + 'us only the licence needed to operate the service for you: to store, '
      + 'process, render, transmit to the recipients you nominate, and '
      + 'produce the completed document and evidence record.' },
    { kind: 'p', text:
      'We do not use your document contents to train models, and we do not '
      + 'sell them. Analytics about use of the product deliberately exclude '
      + 'document contents, field values, signer contact details and tokens.' },
    { kind: 'p', text:
      'You confirm you have the right to upload what you upload and to send '
      + 'it to the recipients you nominate.' },

    { kind: 'h', text: '4. Acceptable use' },
    { kind: 'p', text: 'You must not use the service to:' },
    { kind: 'ul', items: [
      'send agreements you are not entitled to send, or impersonate anybody;',
      'upload malware, or content that is unlawful where you or the '
        + 'recipients are;',
      'circumvent allowances, authentication, rate limits or evidence '
        + 'controls, including by suppressing consent or evidence screens in '
        + 'an embedded integration;',
      'send bulk unsolicited messages through the service;',
      'attempt to access another organization’s data.',
    ] },
    { kind: 'p', text:
      'We may suspend an account that is doing any of these, and will tell you '
      + 'why. Suspension stops new sends; it does not delete completed '
      + 'agreements or block their retrieval.' },

    { kind: 'h', text: '5. Fees' },
    { kind: 'ul', items: [
      'Prepaid credits are charged when you buy them. Credits do not expire '
        + 'and are not refundable once spent on a send. Unspent credits remain '
        + 'on the account.',
      'Automatic top-up, if you turn it on, charges your saved payment method '
        + 'for one further pack when your balance can no longer cover a send. '
        + 'You can turn it off at any time, and a declined top-up pauses it '
        + 'rather than retrying indefinitely.',
      'Annual plans are charged at activation or at the end of the trial, and '
        + 'renew annually until you turn off renewal. The exact first-charge '
        + 'instant is shown before you activate and does not move.',
      'Turning off renewal keeps access through the term you have paid for. We '
        + 'do not refund part-used terms.',
      'Prices exclude tax. Applicable tax is calculated at checkout and shown '
        + 'before you confirm.',
    ] },
    { kind: 'p', text:
      'Card details are collected by our payment provider on their own pages. '
      + 'We never receive your card number or security code — only a reference '
      + 'and the brand, last four digits and expiry needed to display it.' },

    { kind: 'h', text: '6. Automatic renewal, and how to stop it' },
    { kind: 'p', text:
      'Annual plans renew automatically. This is set out here in full because '
      + 'California law requires automatic-renewal terms to be disclosed '
      + 'clearly before you buy, consented to separately, and cancellable '
      + 'online without having to call anyone.' },
    { kind: 'ul', items: [
      'A plan renews for a further year at the then-current price unless you '
        + 'turn off renewal before the term ends.',
      'The exact date and time of the first charge is shown before you '
        + 'activate, repeated in the confirmation email, and does not move.',
      'We will send a reminder before a trial converts to a paid term.',
      'You can turn off renewal at any time from Billing in your dashboard, '
        + 'in two clicks and without contacting us. Turning it off keeps your '
        + 'access through the term you have already paid for.',
      'Prepaid credits do not renew and are not a subscription. Automatic '
        + 'top-up, if you switch it on, buys one further pack when your '
        + 'balance can no longer cover a send; it is off by default and can be '
        + 'switched off at any time from the same page.',
    ] },

    { kind: 'h', text: '7. What a signature here is, and is not' },
    { kind: 'p', text:
      'The service records evidence about a signing event. It does not give '
      + 'legal advice, and it makes no claim that any particular agreement is '
      + 'valid or enforceable in any jurisdiction. Some categories of document '
      + 'are commonly excluded from electronic execution or carry extra '
      + 'formalities; determining whether yours is one of them is your '
      + 'responsibility.' },
    { kind: 'note', tone: 'warn', title: 'No cryptographic seal is applied',
      text: 'Agreements are finalized under an evidence-only assurance '
        + 'profile. No cryptographic PDF seal, externally trusted certificate '
        + 'or independent third-party timestamp is applied, and every artifact '
        + 'reports those three checks as "not applied" rather than as '
        + 'passing. A hash proves bytes are unchanged; it does not establish '
        + 'identity or intent.' },

    { kind: 'h', text: '8. Retention, export and wind-down' },
    { kind: 'p', text:
      'We aim to keep completed agreements and their evidence retrievable for '
      + 'seven years from completion, including after cancellation. That is a '
      + 'target rather than a guarantee: no small service can honestly promise '
      + 'seven years of storage it may not be around to provide, and we would '
      + 'rather state the limit than make a commitment we might not be able to '
      + 'fund.' },
    { kind: 'p', text:
      'What is not conditional is the export. You can download a complete '
      + 'evidence package at any time while your account is active, and it '
      + 'verifies offline — without this service, without an account and '
      + 'without us. If we ever wind the service down we will give you notice '
      + 'and a window to export before access ends. Keep your own copy of '
      + 'anything that matters to you.' },
    { kind: 'p', text:
      'Deletion requests for completed agreements are recorded and evaluated '
      + 'against the retention period and any legal hold in force. See the '
      + 'Privacy Policy for how this interacts with erasure rights.' },

    { kind: 'h', text: '9. Availability' },
    { kind: 'p', text:
      'We do not currently offer a service level agreement. The service is '
      + 'provided on a reasonable-efforts basis, and we may change, suspend or '
      + 'discontinue features. Where a change removes something you rely on we '
      + 'will give reasonable notice.' },

    { kind: 'h', text: '10. Liability' },
    { kind: 'p', text:
      'To the extent permitted by law, we are not liable for indirect or '
      + 'consequential loss, lost profits, or loss arising from a document '
      + 'being unenforceable, misaddressed or disputed. Our total liability in '
      + 'any twelve-month period is limited to the amount you paid us in that '
      + 'period. Nothing here limits liability that cannot lawfully be '
      + 'limited.' },

    { kind: 'h', text: '11. Termination' },
    { kind: 'p', text:
      'You may stop using the service at any time; cancellation is '
      + 'self-service. We may terminate for material breach of these terms, '
      + 'with notice where practicable. On termination you keep retrieval and '
      + 'export access to completed agreements for as long as we retain them, '
      + 'subject to section 8.' },

    { kind: 'h', text: '12. Changes to these terms' },
    { kind: 'p', text:
      'We may update these terms. Material changes will be notified before '
      + 'they take effect, and the version you accepted at activation is '
      + 'recorded against your subscription so it can be identified later.' },

    { kind: 'h', text: '13. Governing law' },
    { kind: 'p', text:
      `These terms are governed by ${COMPANY.governingLaw}.` },

    { kind: 'h', text: '14. Contact' },
    { kind: 'p', text:
      `${COMPANY.entityLong}. Contact: ${COMPANY.contact}, or see the `
      + `support page at /support.` },
  ],
};

export const PRIVACY: LegalDocument = {
  slug: 'privacy',
  title: 'Privacy Policy',
  description:
    'What personal data eSignLoop collects, why, who it is shared with, how '
    + 'long it is kept, and the one place where an append-only evidence '
    + 'record limits what erasure can reach.',
  updated: '2026-10-25',
  summary: [
    'We do not sell or share personal information, and never have.',
    'Signer names, email addresses, IP addresses and user agents are recorded '
      + 'as evidence — that is the point of the product.',
    'We never receive card numbers. The payment provider collects them.',
    'Signing links and one-time codes are stored only as hashes, never in '
      + 'readable form.',
    'The audit ledger is append-only, so erasure of completed agreements is '
      + 'constrained by retention and legal hold.',
    'Analytics is optional, off until you allow it, and never runs on a page '
      + 'whose link is a credential.',
  ],
  body: [
    DRAFT_NOTICE,
    { kind: 'p', text:
      `${COMPANY.entity} operates eSignLoop. This policy explains what we do `
      + `with personal data. Where you are a customer, you are the controller `
      + `of the signer data you put into the service and we act as processor `
      + `on your behalf; for your own account data we are the controller.` },

    { kind: 'h', text: '1. What we collect' },
    { kind: 'table',
      head: ['Data', 'Why'],
      rows: [
        ['Account name, email, organization', 'To provide the account and contact you'],
        ['Authentication factors', 'To secure administrative access'],
        ['Documents you upload and the values entered into them',
         'To render, send and complete the agreement'],
        ['Recipient names, email addresses and optional phone numbers',
         'To deliver the agreement and record who it went to'],
        ['IP address and user agent at each signing event',
         'Evidence: part of the audit record of who acted and from where'],
        ['Timestamps and authentication outcomes',
         'Evidence: when each step happened and whether identity checks passed'],
        ['Consent version accepted', 'Evidence: what wording the signer agreed to'],
        ['Payment reference, card brand, last four digits, expiry',
         'To charge the subscription or credits and to display the method'],
        ['Product usage events', 'To understand how the product is used'],
        ['Analytics page views (optional, with consent)',
         'To see which pages get used — reported as a route pattern, never a URL'],
      ] },
    { kind: 'note', tone: 'info', title: 'What we do not collect',
      text: 'We never receive card numbers or security codes — those are '
        + 'entered on the payment provider’s own page. Signing links and '
        + 'one-time passcodes are stored only as cryptographic hashes and are '
        + 'redacted from logs, events and analytics. Usage analytics exclude '
        + 'document contents, field values, signer contact details and tokens '
        + 'by design.' },

    { kind: 'h', text: '2. Why we are allowed to process it' },
    { kind: 'ul', items: [
      'Performance of a contract: operating the account you signed up for.',
      'Legitimate interests: securing the service, preventing abuse, and '
        + 'producing the evidence record that is the purpose of the product.',
      'Legal obligation: retaining records where law requires it.',
      'Consent: where a signer consents to sign electronically, recorded with '
        + 'the version of the wording they were shown.',
    ] },

    { kind: 'h', text: '3. Who we share it with' },
    { kind: 'p', text:
      'We use a small number of sub-processors to run the service. Each '
      + 'receives only what it needs:' },
    { kind: 'table',
      head: ['Sub-processor', 'Purpose', 'What it receives'],
      rows: [
        ['Email delivery provider', 'Sending invitations, passcodes and copies',
         'Recipient email address, subject and message body'],
        ['Payment provider', 'Collecting card details and taking payment',
         'Card details entered directly by you, and billing contact'],
        ['Hosting and database provider', 'Running the service',
         'All service data, encrypted in transit'],
        ['Anthropic', 'Suggesting where signature and other fields belong',
         'Selected lines of document text — see below'],
      ] },
    { kind: 'p', text:
      'Field suggestion is the one feature that sends document text outside '
      + 'the service, so it is worth being exact about what leaves. We send '
      + 'the lines that look like somewhere a person writes — labels such as '
      + '“Signature:”, “Date:” or “Name:”, the blanks beside them, and the '
      + 'couple of lines either side — together with their position on the '
      + 'page. On an ordinary agreement that is well under one per cent of '
      + 'the document: the clauses, the commercial terms and the schedules '
      + 'stay with us.' },
    { kind: 'p', text:
      'There is one exception, and we would rather state it than bury it. If '
      + 'those lines find nothing — an unusual layout, or a document in a '
      + 'language our matching does not recognise — we send the pages a '
      + 'signature block is most likely to be on, in full, so the feature '
      + 'still works. That is a small number of pages, never the whole '
      + 'document.' },
    { kind: 'p', text:
      'We do not sell personal data, and neither we nor Anthropic use '
      + 'document contents to train models. We disclose data to authorities '
      + 'only where legally required, and will tell you unless prohibited.' },

    { kind: 'h', text: '4. How long we keep it' },
    { kind: 'p', text:
      'We aim to keep completed agreements and their evidence for seven years '
      + 'from completion. That is a target rather than a guarantee — see the '
      + 'Terms of Service, section 7. Drafts that are never sent are deleted '
      + 'on a shorter schedule, and preparation work by visitors who never '
      + 'create an account is deleted sooner still.' },

    { kind: 'h', text: '5. Your rights, and one honest limit' },
    { kind: 'p', text:
      'Depending on where you are, you may have rights to access, correct, '
      + 'export, restrict or erase personal data, and to object to processing. '
      + 'Export is built in: you can download your data at any time without '
      + 'asking us.' },
    { kind: 'note', tone: 'warn', title: 'Erasure and the evidence record',
      text: 'The audit ledger is append-only by design — entries cannot be '
        + 'edited or removed, because a record that can be quietly rewritten '
        + 'is not evidence. This means erasure of a completed agreement is '
        + 'constrained: requests are recorded and evaluated against the '
        + 'retention period and any legal hold in force, and may be deferred '
        + 'rather than actioned immediately. We would rather tell you this up '
        + 'front than discover it together later.' },
    { kind: 'p', text:
      `To exercise a right, contact ${COMPANY.contact}. If you are a signer `
      + `rather than an account holder, the organization that sent you the `
      + `agreement is the controller of that data, and we will direct your `
      + `request to them.` },

    { kind: 'h', text: '6. Security' },
    { kind: 'ul', items: [
      'Signing links, one-time codes, sessions and API keys are stored as '
        + 'hashes only, and are redacted from logs.',
      'Uploaded documents are parsed in a separate process with its own '
        + 'memory, CPU and time limits, replaced after each document.',
      'Organization scoping is enforced on every read in the service layer, '
        + 'not by keeping identifiers secret.',
      'Administrative access requires a second authentication factor.',
      'Retrieving a completed agreement requires a fresh one-time code, even '
        + 'with the emailed link.',
    ] },

    { kind: 'h', text: '7. California privacy rights' },
    { kind: 'p', text:
      'We are a California company, so this section applies to everyone, not '
      + 'only to California residents.' },
    { kind: 'p', text:
      'In the twelve months before the date above we collected the categories '
      + 'of personal information listed in section 1: identifiers (name, '
      + 'email, IP address), commercial information (what you bought), '
      + 'internet activity (how the product was used), and the contents of '
      + 'documents you chose to upload. We collect it for the purposes in '
      + 'section 2 and disclose it only to the sub-processors in section 3.' },
    { kind: 'note', tone: 'ok', title: 'We do not sell or share your data',
      text: 'We do not sell personal information, and we do not share it for '
        + 'cross-context behavioural advertising, as those terms are used in '
        + 'the California Consumer Privacy Act. We have never done so. There '
        + 'is therefore no "Do Not Sell or Share My Personal Information" link '
        + 'to offer you, and we would rather explain that than add a link that '
        + 'does nothing. We also do not disclose personal information to third '
        + 'parties for their own direct marketing, so there is nothing to '
        + 'report under California Civil Code section 1798.83.' },
    { kind: 'p', text:
      'Californian residents have rights to know, delete, correct, and to '
      + 'limit the use of sensitive personal information, and we will not '
      + 'discriminate against you for exercising them. Two honest notes: we do '
      + 'not collect sensitive personal information as the Act defines it, so '
      + 'there is nothing to limit; and deletion of completed agreements is '
      + 'constrained by the retention period and the append-only evidence '
      + 'record described in section 5.' },
    { kind: 'p', text:
      'Whether we currently meet the revenue and volume thresholds that make '
      + 'every provision of the Act apply to us is a question of our size, not '
      + 'of our intentions. We operate to the standards above regardless.' },

    { kind: 'h', text: '8. Cookies and analytics' },
    { kind: 'p', text:
      'We set a session cookie when you sign in. It is necessary for the '
      + 'service to work and is not optional.' },
    { kind: 'p', text:
      'We also offer optional product analytics, provided by Google Analytics '
      + 'through Firebase. It is off until you choose to allow it. Nothing is '
      + 'loaded, no identifier is set and no request is made to Google before '
      + 'that choice — declining means the measurement code is never fetched, '
      + 'not that it is fetched and asked to behave.' },
    { kind: 'p', text:
      'If you allow it, we record which pages are used and in what order, '
      + 'along with the coarse information a browser sends with any request. '
      + 'Three limits are set in code rather than promised: advertising '
      + 'identifiers and cross-site signals are disabled, IP addresses are '
      + 'truncated, and automatic page-view collection is switched off in '
      + 'favour of reporting a route pattern.' },
    { kind: 'note', tone: 'ok', title: 'Your URLs are never sent',
      text: 'Signing, embedded and completed-copy links contain a token that '
        + 'anyone holding can use to open the agreement, and an agreement id '
        + 'is somebody\u2019s data even when it is not a token. So analytics '
        + 'does not run at all on those pages, and everywhere else we report '
        + 'the shape of the route — "/blog/:slug", never the article you read; '
        + '"/app/envelopes/:id", never which agreement. A path we cannot '
        + 'reduce to a known pattern is not reported at all.' },
    { kind: 'p', text:
      'You can change your mind at any time using the analytics control in '
      + 'the footer of any page. Withdrawing takes effect immediately and for '
      + 'the rest of that session. We ask again if we change materially what '
      + 'we are asking for.' },

    { kind: 'h', text: '9. International transfers' },
    { kind: 'p', text:
      'Our sub-processors may process data outside your country. Where that '
      + 'happens we rely on the transfer mechanisms available to us, and the '
      + 'current list of sub-processors and their locations is available on '
      + 'request.' },

    { kind: 'h', text: '10. Changes and contact' },
    { kind: 'p', text:
      `We will post material changes to this policy before they take effect. `
      + `Contact: ${COMPANY.entityLong}, ${COMPANY.contact}. The support `
      + `page at /support explains how privacy requests are handled.` },
  ],
};

export const LEGAL_DOCUMENTS: LegalDocument[] = [TERMS, PRIVACY];

export function legalBySlug(slug: string | undefined): LegalDocument | undefined {
  return LEGAL_DOCUMENTS.find((doc) => doc.slug === slug);
}
